import React, { useState, useMemo } from 'react';
import { AlertCircle, X, Laptop, Search, SlidersHorizontal } from "lucide-react";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import AppCard from '../AppCard';
import CategorySection from './components/CategorySection';
import AdminMenu from '../../Admin/AdminMenu';
import { useAppFiltering } from './hooks/useAppFiltering';

// Style constants for easy opacity adjustment
const STYLES = {
  BACKDROP: {
    background: 'bg-black/10',
    blur: 'backdrop-blur-sm'
  },
  CONTAINER: {
    background: 'bg-gray-900/70',
    blur: 'backdrop-blur-md',
    border: 'border border-white/5'
  },
  HEADER: {
    background: 'bg-gray-900/40',
    border: 'border-b border-gray-800/50'
  }
};

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center h-full text-white">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
    <p className="text-lg font-semibold mb-2">Loading applications...</p>
  </div>
);

const FilterPanel = ({ 
  categories, 
  enabledCategories, 
  onToggleCategory, 
  showCategories, 
  onToggleShowCategories 
}) => {
  return (
    <div className="absolute right-0 top-full mt-2 bg-gray-800/90 backdrop-blur rounded-lg shadow-lg border border-gray-700/50 p-4 z-50 w-64">
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-700">
        <span className="text-white font-medium">Filters</span>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-400">Categories</span>
          <Switch
            checked={showCategories}
            onCheckedChange={onToggleShowCategories}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        {categories.map(category => (
          <label key={category} className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={enabledCategories.includes(category)}
              onChange={() => onToggleCategory(category)}
              className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <span className="text-gray-300">{category}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const AppGallery = ({ 
  isVisible, 
  onClose, 
  launchApp, 
  apps = [], 
  loading, 
  error,
  onVersionSwitch,
  isEmbedded = false,
  spaceId = null,
  isDefaultSpace = false,  // NEW: if true, show all apps
  launchSDK
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // Filter apps by space if spaceId is provided
  // Default space shows ALL apps, custom spaces only show assigned apps
  const spaceFilteredApps = useMemo(() => {
    // No space filter or default space = show all apps
    if (!spaceId || isDefaultSpace) return apps;
    
    // Custom space: only show apps explicitly assigned to this space
    return apps.filter(app => {
      if (!app.space_ids || app.space_ids.length === 0) return false;
      return app.space_ids.includes(spaceId);
    });
  }, [apps, spaceId, isDefaultSpace]);

  const {
    searchTerm,
    setSearchTerm,
    showCategories,
    setShowCategories,
    expandedCategories,
    setExpandedCategories,
    enabledCategories,
    setEnabledCategories,
    categories,
    processedApps,
  } = useAppFiltering(spaceFilteredApps);

  const handleLaunch = (app) => {
    if (app.plugin_type === 'LINK') {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    } else {
      launchApp(app);
    }
    if (!isEmbedded) {
      onClose();
    }
  };

  const toggleCategory = (category) => {
    setEnabledCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleExpanded = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (!isVisible) return null;

  // Embedded version (for use in tabs)
  if (isEmbedded) {
    return (
      <div className="h-full flex flex-col bg-gray-900">
        {/* Header */}
        <div className={`p-4 ${STYLES.HEADER.background} ${STYLES.HEADER.border}`}>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800/90 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="relative">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilters(!showFilters);
                }}
                className="flex items-center space-x-2"
              >
                <SlidersHorizontal size={20} />
                <span>Filters</span>
              </Button>
              
              {showFilters && (
                <FilterPanel
                  categories={categories}
                  enabledCategories={Array.from(enabledCategories)}
                  onToggleCategory={toggleCategory}
                  showCategories={showCategories}
                  onToggleShowCategories={() => setShowCategories(!showCategories)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 md:p-8 overflow-auto">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <AlertCircle className="w-12 h-12 mb-4 text-red-500" />
              <p className="text-lg font-semibold mb-2">Failed to load applications</p>
              <p className="text-gray-400">{error}</p>
            </div>
          ) : processedApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <Laptop className="w-12 h-12 mb-4 text-gray-500" />
              <p className="text-lg font-semibold mb-2">No applications found</p>
              <p className="text-gray-400">Try adjusting your search or filters</p>
            </div>
          ) : showCategories ? (
            processedApps.map(([category, categoryApps]) => (
              <CategorySection
                key={category}
                title={category}
                apps={categoryApps}
                isExpanded={expandedCategories.has(category)}
                onToggleExpand={() => toggleExpanded(category)}
                onLaunch={handleLaunch}
                onVersionSwitch={onVersionSwitch}
              />
            ))
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {processedApps.map((app) => (
                <AppCard
                  key={app.id}
                  app={app}
                  onLaunch={() => handleLaunch(app)}
                  onVersionSwitch={onVersionSwitch}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original modal version
  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center ${STYLES.BACKDROP.background} ${STYLES.BACKDROP.blur}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
          setShowFilters(false);
        }
      }}
    >
      <div 
        className={`relative rounded-lg shadow-xl overflow-hidden ${STYLES.CONTAINER.background} ${STYLES.CONTAINER.blur} ${STYLES.CONTAINER.border}`}
        style={{
          width: '75vw',
          height: '90vh',
        }}
      >
        {/* Header */}
        <div className={`absolute top-0 left-0 right-0 p-4 z-10 ${STYLES.HEADER.background} ${STYLES.HEADER.border}`}>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800/90 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="relative">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilters(!showFilters);
                }}
                className="flex items-center space-x-2"
              >
                <SlidersHorizontal size={20} />
                <span>Filters</span>
              </Button>
              
              {showFilters && (
                <FilterPanel
                  categories={categories}
                  enabledCategories={Array.from(enabledCategories)}
                  onToggleCategory={toggleCategory}
                  showCategories={showCategories}
                  onToggleShowCategories={() => setShowCategories(!showCategories)}
                />
              )}
            </div>
            {/* Admin Menu */}
            <div className="flex items-center">
              <AdminMenu theme="dark" onLaunchSDK={launchSDK} />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800/80 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400 hover:text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-full pt-20 pb-4 px-3 md:px-8 overflow-auto">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <AlertCircle className="w-12 h-12 mb-4 text-red-500" />
              <p className="text-lg font-semibold mb-2">Failed to load applications</p>
              <p className="text-gray-400">{error}</p>
            </div>
          ) : processedApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <Laptop className="w-12 h-12 mb-4 text-gray-500" />
              <p className="text-lg font-semibold mb-2">No applications found</p>
              <p className="text-gray-400">Try adjusting your search or filters</p>
            </div>
          ) : showCategories ? (
            processedApps.map(([category, categoryApps]) => (
              <CategorySection
                key={category}
                title={category}
                apps={categoryApps}
                isExpanded={expandedCategories.has(category)}
                onToggleExpand={() => toggleExpanded(category)}
                onLaunch={handleLaunch}
                onVersionSwitch={onVersionSwitch}
              />
            ))
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {processedApps.map((app) => (
                <AppCard
                  key={app.id}
                  app={app}
                  onLaunch={() => handleLaunch(app)}
                  onVersionSwitch={onVersionSwitch}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppGallery;